import queryString from 'query-string';
import { useCallback } from 'react';

import type { ApiJobSynonymFamilyId } from 'api/types/ApiTypedId';
import {
  useSearchActions,
  useSearchGetters,
} from 'modules/search/contexts/SearchContext';
import { searchConvertFiltersByIdToFiltersByName } from 'modules/search/helpers/searchConvertFiltersByIdToFiltersByName';
import { searchConvertFiltersByNameToFiltersById } from 'modules/search/helpers/searchConvertFiltersByNameToFiltersById';
import type { SearchFacet } from 'modules/search/types/SearchFacet';
import type { SearchFiltersById } from 'modules/search/types/SearchFiltersById';
import type { SearchType } from 'modules/search/types/SearchType';
import { truncateToUTF8Bytes } from 'utils/string/truncateToUTF8Bytes';

type Args = {
  searchType: SearchType;
  searchParams: URLSearchParams;
  searchFacets: SearchFacet[];
  familyId?: ApiJobSynonymFamilyId | null;
};

export function useAssignSearchPageUrlParametersToZustandStore() {
  const { getSearchPageIndex, getSearchQuery, getSearchRadius, getSearchSort } =
    useSearchGetters();

  const {
    setSearchFilters,
    setSearchFiltersByName,
    setSearchPageIndex,
    setSearchQuery,
    setSearchRadius,
    setSearchSort,
  } = useSearchActions();

  const assignSearchPageUrlParametersToZustandStore = useCallback(
    ({ searchType, searchParams, searchFacets, familyId }: Args) => {
      /*
       * Query
       */
      const urlQuery = searchParams.get('q') || '';
      const truncatedUrlQuery = truncateToUTF8Bytes(urlQuery, 512);

      if (truncatedUrlQuery !== getSearchQuery()) {
        setSearchQuery(truncatedUrlQuery);
      }

      /*
       * Filters
       */

      // Object.fromEntries(searchParams.entries()) does not work properly with array params
      const parsedParams = queryString.parse(searchParams.toString()) as Record<
        string,
        string | string[]
      >;

      const filtersById: SearchFiltersById = {
        type: searchType,
        ...searchConvertFiltersByNameToFiltersById(searchFacets, parsedParams),
      };

      if (parsedParams.synonymId && familyId) {
        filtersById.jobFamilyFacet = [familyId];
      }

      setSearchFilters(filtersById);
      setSearchFiltersByName({
        ...searchConvertFiltersByIdToFiltersByName(filtersById),
      });

      /*
       * Sort
       */
      const urlSort =
        searchParams.get('sort') === 'newest' ? 'newest' : 'relevance';
      const reduxSort = getSearchSort() || 'relevance';

      if (urlSort !== reduxSort) {
        setSearchSort(urlSort);
      }

      /*
       * Page Index
       */
      const urlPageIndex = parseInt(searchParams.get('page') || '1', 10) - 1;

      if (urlPageIndex !== getSearchPageIndex()) {
        setSearchPageIndex(urlPageIndex);
      }

      /*
       * Radius
       */
      const urlRadius = searchParams.get('radius');
      const reduxRadius = getSearchRadius();

      if (reduxRadius && !urlRadius) setSearchRadius(undefined);
      if (urlRadius && urlRadius !== reduxRadius) setSearchRadius(urlRadius);
    },
    [
      getSearchPageIndex,
      getSearchQuery,
      getSearchRadius,
      getSearchSort,
      setSearchFilters,
      setSearchFiltersByName,
      setSearchPageIndex,
      setSearchQuery,
      setSearchRadius,
      setSearchSort,
    ],
  );

  return { assignSearchPageUrlParametersToZustandStore };
}
